/**
 * AiAgentSocketService — socket.io client wrapper for the /ai-agents namespace.
 *
 * Manages connection lifecycle and exposes typed Observable streams for each event.
 */
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter } from 'rxjs';
import { io, Socket } from 'socket.io-client';

import { AUMICO_AI_AGENTS_CONFIG, AumicoAiAgentsConfig } from '../aumico-ai-agents.config';
import {
    AiJobProgressEvent,
    AiJobCompletedEvent,
    AiChatMessageEvent,
} from '../interfaces/ai-agent-progress-event.interface';

/** Event names — must match the backend constants */
const AI_JOB_PROGRESS = 'ai:job:progress';
const AI_JOB_COMPLETED = 'ai:job:completed';
const AI_CHAT_MESSAGE = 'ai:chat:message';

@Injectable()
export class AiAgentSocketService implements OnDestroy {
    private socket: Socket | null = null;

    private readonly _connected$ = new BehaviorSubject<boolean>(false);
    private readonly _progress$ = new Subject<AiJobProgressEvent>();
    private readonly _completed$ = new Subject<AiJobCompletedEvent>();
    private readonly _chatMessage$ = new Subject<AiChatMessageEvent>();

    /** Whether the WebSocket is currently connected */
    readonly isConnected$: Observable<boolean> = this._connected$.asObservable();

    constructor(
        @Inject(AUMICO_AI_AGENTS_CONFIG) private readonly config: AumicoAiAgentsConfig,
    ) {}

    /**
     * Connect to the /ai-agents namespace with a Firebase ID token.
     * If already connected, disconnects first.
     */
    connect(token: string): void {
        this.disconnect();

        this.socket = io(`${this.config.wsGateway}/ai-agents`, {
            path: '/ai-agents/socket.io',
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 2000,
        });

        this.socket.on('connect', () => {
            this._connected$.next(true);
        });

        this.socket.on('disconnect', () => {
            this._connected$.next(false);
        });

        this.socket.on(AI_JOB_PROGRESS, (data: AiJobProgressEvent) => {
            this._progress$.next(data);
        });

        this.socket.on(AI_JOB_COMPLETED, (data: AiJobCompletedEvent) => {
            this._completed$.next(data);
        });

        this.socket.on(AI_CHAT_MESSAGE, (data: AiChatMessageEvent) => {
            this._chatMessage$.next(data);
        });
    }

    /** Disconnect from the WebSocket */
    disconnect(): void {
        if (this.socket) {
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
            this._connected$.next(false);
        }
    }

    /** Observable stream of progress events filtered for a specific session */
    onJobProgress$(sessionId: string): Observable<AiJobProgressEvent> {
        return this._progress$.asObservable().pipe(
            filter((e) => e.sessionId === sessionId),
        );
    }

    /** Observable stream of completion events filtered for a specific session */
    onJobCompleted$(sessionId: string): Observable<AiJobCompletedEvent> {
        return this._completed$.asObservable().pipe(
            filter((e) => e.sessionId === sessionId),
        );
    }

    /** Observable stream of chat messages filtered for a specific session */
    onChatMessage$(sessionId: string): Observable<AiChatMessageEvent> {
        return this._chatMessage$.asObservable().pipe(
            filter((e) => e.sessionId === sessionId),
        );
    }

    /** All progress events (unfiltered) — used by effects */
    get allProgress$(): Observable<AiJobProgressEvent> {
        return this._progress$.asObservable();
    }

    /** All completed events (unfiltered) — used by effects */
    get allCompleted$(): Observable<AiJobCompletedEvent> {
        return this._completed$.asObservable();
    }

    ngOnDestroy(): void {
        this.disconnect();
    }
}
